/* eslint-disable max-lines, max-lines-per-function */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Skeleton } from 'primereact/skeleton';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './GrossMarginReport.scss';
import { useAuth } from '../../../Hooks/useAuth';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { GROSS_MARGIN_REPORT } from '../../../Api/resources/endPoint';
import { Tooltip } from 'primereact/tooltip';
import lottie from 'lottie-web';
import { toast } from 'react-toastify';

const isNonEmptyString = (value) => typeof value === 'string' && value.trim() !== '';

const flattenMenuItems = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.flatMap((menuItem) => (Array.isArray(menuItem?.items) ? menuItem.items : []))
    : []
);

const getFilterMenuItem = (tableConfiguration = [], field) => {
  const column = Array.isArray(tableConfiguration)
    ? tableConfiguration.find((item) => item?.field === field)
    : null;

  if (!column) return null;

  return flattenMenuItems(column.menuItems).find(
    (item) => item?.type === 'filter' && item?.field === field,
  ) || null;
};

const getSelectedOptionLabel = (
  selectedValue,
  { allowIdentifierFallback = true } = {},
) => {
  if (!selectedValue) return '';
  if (typeof selectedValue === 'string') return selectedValue.trim();
  if (typeof selectedValue === 'number') return allowIdentifierFallback ? `${selectedValue}` : '';

  if (isNonEmptyString(selectedValue.name)) return selectedValue.name.trim();
  if (isNonEmptyString(selectedValue.label)) return selectedValue.label.trim();
  if (!allowIdentifierFallback) return '';
  if (selectedValue.value !== undefined && selectedValue.value !== null) {
    return `${selectedValue.value}`.trim();
  }
  if (selectedValue.id !== undefined && selectedValue.id !== null) {
    return `${selectedValue.id}`.trim();
  }

  return '';
};

const getReadableSelectedLabel = (selectedValue = null) => {
  const selectedLabel = getSelectedOptionLabel(selectedValue, {
    allowIdentifierFallback: false,
  });

  if (!selectedLabel) {
    return '';
  }

  if (!selectedValue || typeof selectedValue !== 'object') {
    return /^\d+$/.test(selectedLabel) ? '' : selectedLabel;
  }

  const rawIdentifiers = [
    selectedValue.id,
    selectedValue.value,
  ]
    .filter((value) => value !== undefined && value !== null && `${value}`.trim() !== '')
    .map((value) => `${value}`.trim());

  return rawIdentifiers.includes(selectedLabel.trim()) ? '' : selectedLabel;
};

const cloneSelectedValues = (selectedValues = []) => (
  Array.isArray(selectedValues)
    ? selectedValues
      .filter(Boolean)
      .map((selectedValue) => (
        typeof selectedValue === 'object'
          ? { ...selectedValue }
          : {
            id: selectedValue,
            value: selectedValue,
            label: `${selectedValue}`,
            name: `${selectedValue}`,
          }
      ))
    : []
);

const cloneSelectedValue = (selectedValue = null) => {
  if (!selectedValue) return null;
  if (typeof selectedValue === 'object') return { ...selectedValue };

  return {
    id: selectedValue,
    value: selectedValue,
    label: `${selectedValue}`,
    name: `${selectedValue}`,
  };
};

const getOptionLabelFromChoices = (selectedValue, options = []) => {
  if (!selectedValue || !Array.isArray(options) || options.length === 0) {
    return '';
  }

  const selectedOptionKeys = new Set(
    [
      selectedValue?.id,
      selectedValue?.value,
      selectedValue,
    ]
      .filter((value) => value !== undefined && value !== null && `${value}`.trim() !== '')
      .map((value) => `${value}`.trim()),
  );

  const matchedOption = options.find((option) => (
    [
      option?.id,
      option?.value,
    ]
      .filter((value) => value !== undefined && value !== null && `${value}`.trim() !== '')
      .map((value) => `${value}`.trim())
      .some((value) => selectedOptionKeys.has(value))
  ));

  return getSelectedOptionLabel(matchedOption, { allowIdentifierFallback: false });
};

const getProjectNameFromFilterItem = (filterItem = null) => {
  if (!filterItem || typeof filterItem !== 'object') {
    return '';
  }

  return getReadableSelectedLabel(filterItem.selectedValue)
    || getOptionLabelFromChoices(filterItem.selectedValue, filterItem.options);
};

const parseRequestPayload = (response = {}) => {
  const requestData = response?.config?.data;

  if (!requestData) return {};

  if (typeof requestData === 'string') {
    try {
      return JSON.parse(requestData);
    } catch {
      return {};
    }
  }

  return typeof requestData === 'object' ? requestData : {};
};

const getProjectNameFromRequestPayload = (response = {}) => {
  const requestPayload = parseRequestPayload(response);
  const payload = requestPayload?.payload;
  const candidateContainers = [
    requestPayload?.filter,
    requestPayload?.filters,
    requestPayload?.filterCriteria,
    requestPayload?.additionalPayloadData,
    payload?.filter,
    payload?.filters,
    payload?.filterCriteria,
    payload?.additionalPayloadData,
    payload,
    requestPayload,
  ].filter(Boolean);

  const projectNameCandidateValue = candidateContainers
    .map((container) => container?.projectName)
    .find(Boolean);
  const projectNameCandidate = Array.isArray(projectNameCandidateValue)
    ? projectNameCandidateValue[0]
    : projectNameCandidateValue;

  if (!projectNameCandidate) {
    return '';
  }

  if (projectNameCandidate?.selectedValue) {
    return getReadableSelectedLabel(projectNameCandidate.selectedValue)
      || getOptionLabelFromChoices(
        projectNameCandidate.selectedValue,
        projectNameCandidate.options,
      );
  }

  if (Array.isArray(projectNameCandidate?.selectedValues)) {
    return getReadableSelectedLabel(projectNameCandidate.selectedValues[0])
      || getOptionLabelFromChoices(
        projectNameCandidate.selectedValues[0],
        projectNameCandidate.options,
      );
  }

  return getReadableSelectedLabel(projectNameCandidate);
};

const getReportTitleFromResponse = (response = {}) => {
  const responseData = response?.data && typeof response.data === 'object' ? response.data : {};
  const payload = responseData?.data && typeof responseData.data === 'object'
    ? responseData.data
    : responseData;
  const reportTitle = `${payload.reportTitle || payload.title || ''}`.trim();

  return reportTitle.startsWith('Gross Margin Report - ') ? reportTitle : '';
};

const getResponsePayload = (response = {}) => {
  const responseData = response?.data && typeof response.data === 'object' ? response.data : {};

  return responseData?.data && typeof responseData.data === 'object'
    ? responseData.data
    : responseData;
};

const areEqualByValue = (leftValue, rightValue) => (
  JSON.stringify(leftValue) === JSON.stringify(rightValue)
);

const getReportTitleFromConfiguration = (
  response,
  tableConfiguration = [],
  defaultTitle = 'Gross Margin Report',
) => {
  const selectedProjectFilter = getFilterMenuItem(tableConfiguration, 'projectName');
  const responseReportTitle = getReportTitleFromResponse(response);
  const projectName = getProjectNameFromReportTitle(responseReportTitle)
    || getProjectNameFromRequestPayload(response)
    || getProjectNameFromFilterItem(selectedProjectFilter);

  return projectName ? `Gross Margin Report - ${projectName}` : defaultTitle;
};

const getAppliedFilterPayload = (tableConfiguration = []) => {
  const projectVertical = getFilterMenuItem(tableConfiguration, 'projectVertical');
  const projectType = getFilterMenuItem(tableConfiguration, 'projectType');
  const projectStatus = getFilterMenuItem(tableConfiguration, 'projectStatus');
  const projectName = getFilterMenuItem(tableConfiguration, 'projectName');
  const activity = getFilterMenuItem(tableConfiguration, 'activity');
  const total = getFilterMenuItem(tableConfiguration, 'total');
  const dateRange = getFilterMenuItem(tableConfiguration, 'dateRange');

  return {
    projectVertical: cloneSelectedValues(projectVertical?.selectedValues),
    projectType: cloneSelectedValues(projectType?.selectedValues),
    projectStatus: cloneSelectedValues(projectStatus?.selectedValues),
    projectName: cloneSelectedValue(projectName?.selectedValue),
    activity: cloneSelectedValues(activity?.selectedValues),
    total: {
      range: Array.isArray(total?.range) ? [...total.range] : [],
    },
    dateRange: {
      range: Array.isArray(dateRange?.range) ? [...dateRange.range] : [],
    },
  };
};

const getProjectNameFromAppliedFilters = (filterPayload = {}) => (
  getReadableSelectedLabel(filterPayload?.projectName)
);

const sanitizeExportFileSegment = (value, fallback = 'Report') => {
  const normalizedValue = `${value ?? ''}`
    .trim()
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return normalizedValue || fallback;
};

const getProjectNameFromExportFilters = (filters = {}) => {
  if (Array.isArray(filters)) {
    const projectNameFilter = filters.find((filterItem) => filterItem?.field === 'projectName');
    return getReadableSelectedLabel(projectNameFilter?.selectedValue)
      || getSelectedOptionLabel(projectNameFilter?.selectedValue, {
        allowIdentifierFallback: false,
      });
  }

  return getReadableSelectedLabel(filters?.projectName)
    || getSelectedOptionLabel(filters?.projectName, {
      allowIdentifierFallback: false,
    });
};

const buildActivityExportFileName = () => {

  return 'Gross margin Report';
};

const buildActivityExportSheetName = (projectName = '') => (
  `Gross_margin - ${sanitizeExportFileSegment(projectName)}`
);

const getProjectNameFromReportTitle = (reportTitle = '') => {
  const titleParts = `${reportTitle}`.split(' - ');
  return titleParts.length > 1
    ? titleParts.slice(1).join(' - ')
      .trim()
    : '';
};

/* -------------------------------------------------------------------------- */
/*                          Month Range Helpers                               */
/* -------------------------------------------------------------------------- */

const isMonthFilter = (item) => {
  const template = (item?.template || '').toString().toLowerCase();
  const type = (item?.type || '').toString().toLowerCase();
  const pickerType = (item?.pickerType || '').toString().toLowerCase();
  const displayFormat = (item?.displayFormat || '').toString().toLowerCase();

  return (
    template === 'monthcalendar'
    || template === 'month'
    || type === 'month'
    || pickerType === 'month'
    || displayFormat === 'yyyy-mm'
  );
};

const toMonthCalendarValue = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return `${value.getMonth() + 1}/${value.getFullYear()}`;
  }

  const normalized = value.toString().trim();
  if (!normalized) return null;

  const yearMonthMatch = normalized.match(/^(\d{4})-(\d{1,2})$/);
  if (yearMonthMatch) {
    const [, year, month] = yearMonthMatch;
    return `${Number(month)}/${year}`;
  }

  const monthYearMatch = normalized.match(/^(\d{1,2})[/-](\d{4})$/);
  if (monthYearMatch) {
    const [, month, year] = monthYearMatch;
    return `${Number(month)}/${year}`;
  }

  return normalized;
};

const normalizeMonthYearPart = (yearPart) => {
  const normalizedYear = String(yearPart || '').trim();
  if (!normalizedYear) return null;
  if (normalizedYear.length === 2) return `20${normalizedYear}`;
  return /^\d{4}$/.test(normalizedYear) ? normalizedYear : null;
};

const toApiMonthRangeValue = (value, { useEndOfMonth = false } = {}) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const year = value.getFullYear();
    const month = value.getMonth() + 1;
    const day = useEndOfMonth ? new Date(year, month, 0).getDate() : 1;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  const normalized = String(value).trim();
  if (!normalized) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;

  const yearMonthMatch = normalized.match(/^(\d{4})-(\d{1,2})$/);
  if (yearMonthMatch) {
    const [, year, month] = yearMonthMatch;
    const lastDay = new Date(Number(year), Number(month), 0).getDate();
    return `${year}-${String(Number(month)).padStart(2, '0')}-${String(useEndOfMonth ? lastDay : 1).padStart(2, '0')}`;
  }

  const numericMonthYearMatch = normalized.match(/^(\d{1,2})[/-](\d{2}|\d{4})$/);
  if (numericMonthYearMatch) {
    const [, monthPart, yearPart] = numericMonthYearMatch;
    const year = normalizeMonthYearPart(yearPart);
    const month = Number(monthPart);
    if (!year || Number.isNaN(month) || month < 1 || month > 12) return null;
    const lastDay = new Date(Number(year), month, 0).getDate();
    return `${year}-${String(month).padStart(2, '0')}-${String(useEndOfMonth ? lastDay : 1).padStart(2, '0')}`;
  }

  const monthNameYearMatch = normalized.match(/^([A-Za-z]{3,9})[-/\s](\d{2}|\d{4})$/);
  if (monthNameYearMatch) {
    const [, monthName, yearPart] = monthNameYearMatch;
    const year = normalizeMonthYearPart(yearPart);
    if (!year) return null;
    const parsed = new Date(`${monthName} 1, ${year}`);
    if (Number.isNaN(parsed.getTime())) return null;
    return toApiMonthRangeValue(parsed, { useEndOfMonth });
  }

  const parsedDate = new Date(normalized);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return toApiMonthRangeValue(parsedDate, { useEndOfMonth });
};

const normalizeMonthCalendarMenuItems = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => {
          if (item?.type !== 'filter') return item;
          const normalizedTemplate = (item?.template || '').toString().toLowerCase();
          if (normalizedTemplate === 'daterange') return item;
          if (!isMonthFilter(item)) return item;

          return {
            ...item,
            template: 'monthCalendar',
            selectedValue: toMonthCalendarValue(item?.selectedValue ?? item?.value ?? null),
            calendarOverlayConfig: {
              ...(item?.calendarOverlayConfig || {}),
              panelClassName: 'month-wise-calendar-filter-container',
            },
          };
        })
        : menuItem?.items,
    }))
    : []
);

const applyToolbarButtonClasses = (rootNode) => {
  if (!rootNode) return;

  const toolbarButtons = rootNode.querySelectorAll('.button-group button');
  toolbarButtons.forEach((buttonElement) => {
    const isResetButton = Boolean(buttonElement.querySelector('.pi-refresh'));
    const isFilterButton = buttonElement.classList.contains('table-utility-filter-btn');
    const isDownloadButton = !isFilterButton
      && !isResetButton
      && Boolean(buttonElement.querySelector('svg'));
    buttonElement.classList.toggle('gross-margin-reset-btn', isResetButton);
    buttonElement.classList.toggle('gross-margin-export-btn', isDownloadButton);
  });
};

const DEFAULT_REPORT_TITLE = 'Gross Margin Report';
const DEFAULT_PAGINATION = Object.freeze({
  page: 1,
  limit: 10,
  total: 0,
});
const TABLE_SCROLL_RATIO = 0.7;
const SUMMARY_ROW_ID = 'summary-row';
const INDIAN_NUMBER_FORMATTER = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const REPORT_DATE_HEADER_PATTERN
  = /^(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday)\s+(\d{2}-[A-Za-z]{3}-\d{4})$/;

const fetchBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${GROSS_MARGIN_REPORT}`;
  }

  return `${baseUrl}:${servicePort}${serviceURL}/api${GROSS_MARGIN_REPORT}`;
};

const baseURL = fetchBaseURL();

const getColumnHeaderText = (column = {}) => {
  const headerCandidates = [
    column?.header,
    column?.label,
    column?.filterLabel,
    column?.menuLabel,
    column?.displayLabel,
    column?.field,
  ];

  const headerText = headerCandidates.find((candidate) => typeof candidate === 'string');

  return headerText ? headerText.trim() : '';
};

const isActivityDisplayColumn = (column = {}) => column?.field === 'activity';

const isTotalEffortDisplayColumn = (column = {}) => {
  const headerText = getColumnHeaderText(column).toLowerCase();

  return column?.field === 'total' || headerText.startsWith('total effort');
};

const isDateDisplayColumn = (column = {}) => REPORT_DATE_HEADER_PATTERN.test(
  getColumnHeaderText(column),
);

const getNumericDisplayFields = (tableConfiguration = []) => (
  Array.isArray(tableConfiguration)
    ? tableConfiguration
      .filter((column) => isTotalEffortDisplayColumn(column) || isDateDisplayColumn(column))
      .map((column) => column?.field)
      .filter(Boolean)
    : []
);

const parseNumericValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value !== 'string') {
    return 0;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return 0;
  }

  const isNegativeValue = trimmedValue.startsWith('(') && trimmedValue.endsWith(')');
  const normalizedValue = trimmedValue.replace(/,/g, '').replace(/[()%]/g, '');
  const parsedValue = Number(normalizedValue);

  if (!Number.isFinite(parsedValue)) {
    return 0;
  }

  return isNegativeValue ? -parsedValue : parsedValue;
};

const formatIndianDecimal = (value) => INDIAN_NUMBER_FORMATTER.format(parseNumericValue(value));

const formatNumericFieldsInRow = (row = {}, numericFields = []) => {
  if (!row || typeof row !== 'object') {
    return row;
  }

  const formattedRow = { ...row };

  numericFields.forEach((field) => {
    formattedRow[field] = formatIndianDecimal(row[field]);
  });

  if (Array.isArray(row.children)) {
    formattedRow.children = row.children.map((childRow) => formatNumericFieldsInRow(
      childRow,
      numericFields,
    ));
  }

  return formattedRow;
};

const formatTableData = (tableData = [], numericFields = []) => (
  Array.isArray(tableData)
    ? tableData.map((row) => formatNumericFieldsInRow(row, numericFields))
    : []
);

const renderStackedHeader = (
  primaryLabel,
  secondaryLabel,
  variant = '',
  tertiaryLabel = '',
) => (
  <span className={`gross-margin-header-stack ${variant}`.trim()}>
    <span className="activity-report-header-primary">{primaryLabel}</span>
    <span className="activity-report-header-secondary">{secondaryLabel}</span>
    {tertiaryLabel ? (
      <span className="activity-report-header-tertiary">{tertiaryLabel}</span>
    ) : null}
  </span>
);

const buildDisplayHeader = (column = {}) => {
  const headerText = getColumnHeaderText(column);

  if (isTotalEffortDisplayColumn(column)) {
    return renderStackedHeader(
      'Total effort',
      '(hours)',
      'gross-margin-header-stack--numeric',
    );
  }

  const dateHeaderMatch = headerText.match(REPORT_DATE_HEADER_PATTERN);

  if (dateHeaderMatch) {
    const [, weekDay, dayLabel] = dateHeaderMatch;

    if (column?.isHoliday) {
      return renderStackedHeader(
        'Holiday',
        weekDay,
        'gross-margin-header-stack--numeric',
        dayLabel,
      );
    }

    return renderStackedHeader(
      weekDay,
      dayLabel,
      'gross-margin-header-stack--numeric',
    );
  }

  return headerText;
};

const removeInlineSortIcon = (menuItems = [], shouldHideSortIcon = false) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => (
          item?.type === 'sort' && shouldHideSortIcon
            ? {
              ...item,
              showSortButtonInHeader: false,
            }
            : item
        ))
        : menuItem?.items,
    }))
    : []
);

const normalizeFilterSelectionValue = (selectedValue) => {
  if (selectedValue === null || selectedValue === undefined || selectedValue === '') {
    return null;
  }

  if (typeof selectedValue === 'object' && !Array.isArray(selectedValue)) {
    return selectedValue.value
      ?? selectedValue.id
      ?? selectedValue.name
      ?? selectedValue.label
      ?? null;
  }

  return selectedValue;
};

const normalizeMultiSelectMenuItems = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => {
          if (item?.type !== 'filter') {
            return item;
          }

          if (item?.template !== 'multiSelect' && item?.template !== 'choice') {
            return item;
          }

          const normalizedSelectedValues = Array.isArray(item?.selectedValues)
            ? item.selectedValues
              .map(normalizeFilterSelectionValue)
              .filter((selectedValue) => selectedValue !== null)
            : item?.selectedValues;

          return {
            ...item,
            selectedValues: normalizedSelectedValues,
          };
        })
        : menuItem?.items,
    }))
    : []
);

const applyActivityReportFilterMetadata = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => {
          if (item?.type !== 'filter') {
            return item;
          }

          if (item?.field === 'dateRange') {
            return {
              ...item,
              tableUtilityConfig: {
                ...(item?.tableUtilityConfig || {}),
                maxRangeDays: 31,
                maxRangeValidationMessage: 'Date range has to be within 31 days.',
              },
            };
          }

          return item;
        })
        : menuItem?.items,
    }))
    : []
);

const ensureMenuItems = (tableConfiguration = []) => (
  Array.isArray(tableConfiguration)
    ? tableConfiguration.map((column) => ({
      ...column,
      menuItems: Array.isArray(column?.menuItems) ? column.menuItems : [],
    }))
    : []
);

const transformTableConfiguration = (tableConfiguration = []) => ensureMenuItems(
  tableConfiguration,
).map((column) => {
  const headerText = getColumnHeaderText(column);
  const shouldShowHeaderActions = false;
  const shouldDefaultPinColumn = isActivityDisplayColumn(column);
  const shouldShowPinButton = false;
  const hideInlineSortIcon
    = shouldShowHeaderActions;

  return {
    ...column,
    filter: shouldShowHeaderActions ? true : column?.filter,
    pinnable: shouldShowHeaderActions ? false : column?.pinnable,
    defaultPinned: shouldShowHeaderActions
      ? shouldDefaultPinColumn
      : column?.defaultPinned,
    isPinned: shouldShowHeaderActions
      ? false
      : column?.isPinned,
    filterVisible: shouldShowHeaderActions
      ? false
      : column?.filterVisible,
    pinVisible: shouldShowHeaderActions
      ? shouldShowPinButton
      : column?.pinVisible,
    header: buildDisplayHeader({
      ...column,
      header: headerText,
    }),
    label: column?.label || headerText,
    filterLabel: column?.filterLabel || headerText,
    menuLabel: column?.menuLabel || headerText,
    displayLabel: column?.displayLabel || headerText,
    menuItems: removeInlineSortIcon(
      applyActivityReportFilterMetadata(
        normalizeMultiSelectMenuItems(
          normalizeMonthCalendarMenuItems(column?.menuItems),
        ),
      ),
      hideInlineSortIcon,
    ),
  };
});

const buildSummaryRow = (summary = null) => {
  if (!summary || typeof summary !== 'object' || Object.keys(summary).length === 0) {
    return null;
  }

  return {
    ...summary,
    id: summary.id || SUMMARY_ROW_ID,
    rowConfiguration: {
      ...(summary.rowConfiguration || {}),
      isFrozen: true,
      backgroundColor: summary.rowConfiguration?.backgroundColor || '#eceff5',
    },
  };
};

const syncPaginationFromResponse = (setPagination, response) => {
  const meta = response?.meta && typeof response.meta === 'object' ? response.meta : {};

  setPagination((previousPagination) => ({
    ...previousPagination,
    page: Number(meta.page) > 0 ? Number(meta.page) : previousPagination.page,
    limit: Number(meta.limit) > 0 ? Number(meta.limit) : previousPagination.limit,
    total: Number(meta.total) >= 0 ? Number(meta.total) : previousPagination.total,
  }));
};

// const applyActivityReportHeaderClass = (rootNode) => {
//   if (!rootNode) return;

//   const headers = rootNode.querySelectorAll('.project-overview-header');
//   headers.forEach((headerElement) => {
//     headerElement.classList.add('gross-margin-header');
//   });
// };

const applyActivityTableViewport = (rootNode) => {
  if (!rootNode) return;

  const tableContainers = rootNode.querySelectorAll('.table-container');
  tableContainers.forEach((tableContainerElement) => {
    tableContainerElement.classList.add('gross-margin-table-viewport');
  });
};

const applyBodyCellHoverTitles = (rootNode) => {
  if (!rootNode) return;

  const bodyCells = rootNode.querySelectorAll(
    '.gross-margin-table-utility .p-datatable-tbody > tr > td',
  );

  bodyCells.forEach((td) => {
    const contentElement
      = td.querySelector('.cell-wrapper .ellipses')
        || td.querySelector('.cell-wrapper > span')
        || td.querySelector('span')
        || td.querySelector('div')
        || td;

    const fullText = (contentElement.textContent || '').trim();
    const isTruncated = contentElement.scrollWidth > contentElement.clientWidth;

    if (fullText && isTruncated) {
      td.setAttribute('title', fullText);
      contentElement.setAttribute('title', fullText);
      return;
    }

    td.removeAttribute('title');
    if (contentElement !== td) {
      contentElement.removeAttribute('title');
    }
  });
};

const useActivityReportTableEffects = ({
  containerRef,
  setTableScrollHeight,
}) => {
  useEffect(() => {
    const rootNode = containerRef.current;

    const updateTableClasses = () => {
      applyActivityTableViewport(rootNode);
      // applyActivityReportHeaderClass(rootNode);
      applyBodyCellHoverTitles(rootNode);
      applyToolbarButtonClasses(rootNode);
    };

    const timer = setTimeout(updateTableClasses, 500);
    const observer = new MutationObserver(updateTableClasses);
    const tableContainer = rootNode?.querySelector('.gross-margin-table-utility');

    if (tableContainer) {
      observer.observe(tableContainer, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    const handleResize = () => {
      setTableScrollHeight(`${Math.floor(window.innerHeight * TABLE_SCROLL_RATIO)}px`);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef, setTableScrollHeight]);
};

const useProjectNameTitleSync = ({
  appliedFilterPayload,
  projectNameFilterUrl,
  accessToken,
  utilityUserInfo,
  setReportTitle,
}) => {
  useEffect(() => {
    const selectedProject = appliedFilterPayload?.projectName;
    const selectedProjectId = selectedProject?.id ?? selectedProject?.value ?? null;
    const selectedProjectName = `${selectedProject?.name || selectedProject?.label || ''}`.trim();

    if (!selectedProjectId || !projectNameFilterUrl || !accessToken) {
      return undefined;
    }

    if (selectedProjectName && selectedProjectName !== `${selectedProjectId}`.trim()) {
      setReportTitle(`Gross Margin Report - ${selectedProjectName}`);
      return undefined;
    }

    let isMounted = true;

    const fetchProjectName = async () => {
      try {
        const response = await fetch(projectNameFilterUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            token: accessToken,
          },
          body: JSON.stringify({
            payload: {
              searchQuery: '',
              userInfo: utilityUserInfo,
              ...appliedFilterPayload,
            },
          }),
        });

        const responsePayload = await response.json();
        const projectOptions = Array.isArray(responsePayload?.data) ? responsePayload.data : [];
        const matchedProject = projectOptions.find((projectOption) => (
          [`${projectOption?.id ?? ''}`.trim(), `${projectOption?.value ?? ''}`.trim()]
            .includes(`${selectedProjectId}`.trim())
        ));
        const matchedProjectName = `${matchedProject?.name || matchedProject?.label || ''}`.trim();

        if (isMounted && matchedProjectName) {
          setReportTitle(`Gross Margin Report - ${matchedProjectName}`);
        }
      } catch {
        // Keep the current title if the lookup endpoint fails.
      }
    };

    fetchProjectName();

    return () => {
      isMounted = false;
    };
  }, [
    accessToken,
    appliedFilterPayload,
    projectNameFilterUrl,
    setReportTitle,
    utilityUserInfo,
  ]);
};

const GrossMarginReportInitialState = ({ animationContainer }) => (
  <div className="gross-margin-initial-state">
    <div ref={animationContainer} className="gross-margin-initial-animation" />
    <p className="gross-margin-initial-text">Select the filters above to see the results</p>
  </div>
);

GrossMarginReportInitialState.propTypes = {
  animationContainer: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
};

const LocalSkeleton = () => (
  <div className="gross-margin-skeleton-loader">
    <div className="gross-margin-skeleton-card">
      <div className="gross-margin-skeleton-header">
        <Skeleton width="180px" height="1.5rem" />
        <div className="gross-margin-skeleton-actions">
          <Skeleton width="80px" height="1.5rem" />
          <Skeleton width="80px" height="1.5rem" />
        </div>
      </div>
      <div className="gross-margin-skeleton-body">
        {[1, 2, 3, 4, 5].map((key) => (
          <Skeleton key={key} width="100%" height="2rem" />
        ))}
      </div>
    </div>
  </div>
);

const ToolBarHeading = () => (
  <div className="gross-margin-report-toolbar-heading">
    <span className="gross-margin-report-toolbar-heading-title">
      Gross Margin Report
    </span>
    <div className="gross-margin-report-toolbar-heading-right">
      <span className="gross-margin-report-toolbar-heading-note">
        <strong>Note:</strong> The amounts in the table are in INR format
      </span>
      <span className="gross-margin-report-toolbar-heading-separator" aria-hidden="true" />
    </div>
  </div>
);

const GrossMarginReport = () => {
  const { userProfile } = useAuth();
  const containerRef = useRef(null);
  const tableContainerRef = useRef(null);
  const [tableScrollHeight, setTableScrollHeight] = useState('400px');
  const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [appliedFilterPayload, setAppliedFilterPayload] = useState({});
  const [projectNameFilterUrl, setProjectNameFilterUrl] = useState('');
  const selectedMonthRangeRef = useRef({ fromMonthYear: null, toMonthYear: null });
  const tableConfigInjected = useRef(false);
  const [loading, setLoading] = useState(true);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [animationData, setAnimationData] = useState(null);
  const animationContainer = useRef(null);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const setSelectedMonthRange = useCallback((nextRange) => {
    selectedMonthRangeRef.current = {
      fromMonthYear: nextRange?.fromMonthYear || null,
      toMonthYear: nextRange?.toMonthYear || null,
    };
  }, []);

  const utilityUserInfo = useMemo(() => ({
    emailId: userProfile?.email,
    permissions: userProfile?.permissions,
    roleDetails: userProfile?.roleDetails,
    employeeId: userProfile?.employeeId,
    globalColors: userProfile?.siteConfiguration?.global_colors,
  }), [userProfile]);

  useActivityReportTableEffects({
    containerRef,
    setTableScrollHeight,
  });

  useProjectNameTitleSync({
    appliedFilterPayload,
    projectNameFilterUrl,
    accessToken: userProfile?.access_token,
    utilityUserInfo,
    setReportTitle,
  });

  const handleCellClick = useCallback(({ fieldPath, rowData }) => {
    if (fieldPath !== 'projectName') return;

    const projectId = rowData?.projectId;
    if (!projectId) return;

    const projectDetailsUrl = `/delivery/project-details?projectId=${encodeURIComponent(String(projectId))}`;
    window.open(projectDetailsUrl, '_blank', 'noopener,noreferrer');
  }, []);

  const handleFilteredApiResponse = useCallback((response) => {
    const payload = getResponsePayload(response);
    setHasAppliedFilters(response.data.filterApplied);
    setIsFirstLoad(false);
    const rawTableConfiguration = payload.tableConfiguration;

    // Only inject tableConfiguration on the first call so the table utility
    // keeps its own filter-selection state on subsequent calls.
    // Replacing tableConfiguration every call resets selectedValues and loses multi-select state.
    const shouldInjectConfig = !tableConfigInjected.current
      && Array.isArray(rawTableConfiguration)
      && rawTableConfiguration.length > 0;

    const tableConfiguration = Array.isArray(rawTableConfiguration) && rawTableConfiguration.length > 0
      ? transformTableConfiguration(rawTableConfiguration)
      : [];

    if (shouldInjectConfig) {
      tableConfigInjected.current = true;
    }

    const numericDisplayFields = getNumericDisplayFields(tableConfiguration);
    const rawTableData = Array.isArray(payload.tableData) ? payload.tableData : [];
    const formattedTableData = formatTableData(rawTableData, numericDisplayFields);
    const formattedSummary = formatNumericFieldsInRow(payload.summary, numericDisplayFields);
    const summaryRow = formattedTableData.length > 0 ? buildSummaryRow(formattedSummary) : null;
    const tableData = summaryRow ? [...formattedTableData, summaryRow] : formattedTableData;

    response.data = {
      ...(response?.data || {}),
      ...(shouldInjectConfig ? { tableConfiguration } : {}),
      tableData,
      data: {
        ...payload,
        ...(shouldInjectConfig ? { tableConfiguration } : {}),
        tableData,
      },
    };
    const nextAppliedFilterPayload = getAppliedFilterPayload(tableConfiguration);
    const projectNameFilterItem = getFilterMenuItem(tableConfiguration, 'projectName');
    const nextReportTitle = getReportTitleFromConfiguration(response, tableConfiguration);
    const nextProjectNameFilterUrl = projectNameFilterItem?.url || '';

    setReportTitle((previousTitle) => (
      previousTitle === nextReportTitle ? previousTitle : nextReportTitle
    ));
    setAppliedFilterPayload((previousPayload) => (
      areEqualByValue(previousPayload, nextAppliedFilterPayload)
        ? previousPayload
        : nextAppliedFilterPayload
    ));
    setProjectNameFilterUrl((previousUrl) => (
      previousUrl === nextProjectNameFilterUrl ? previousUrl : nextProjectNameFilterUrl
    ));
    syncPaginationFromResponse(setPagination, response);
    setLoading(false);
  }, []);

  const handlePaginationChange = useCallback((nextPagination) => {
    setPagination(nextPagination);
  }, []);

  const buildSelectedProjectExportFileName = useCallback(() => {
    return buildActivityExportFileName();
  }, [appliedFilterPayload, reportTitle]);

  const buildSelectedProjectExportSheetName = useCallback((exportContext = {}) => {
    const selectedProjectName = getProjectNameFromReportTitle(reportTitle)
      || getProjectNameFromAppliedFilters(appliedFilterPayload)
      || getProjectNameFromExportFilters(exportContext?.filters)
      || 'Report';

    return buildActivityExportSheetName(selectedProjectName);
  }, [appliedFilterPayload, reportTitle]);

  // Add info icon to Project Expense Status header
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    // Shared tooltip
    const tooltip = document.createElement('div');
    tooltip.className = 'project-expense-gm-custom-tooltip';
    tooltip.innerHTML = `
      Project Expense = Travel and <br/>
      Accommodation Cost + Other<br/>
      Cost + Consultant Cost +<br/>
      Software/Hardware Cost
    `;

    document.body.appendChild(tooltip);

    const showTooltip = (event) => {
      const rect = event.target.getBoundingClientRect();

      tooltip.style.display = 'block';
      tooltip.style.top = `${rect.top + window.scrollY - tooltip.offsetHeight - 8}px`;
      tooltip.style.left = `${rect.left + window.scrollX + rect.width / 2}px`;
      tooltip.style.transform = 'translateX(-50%)';
    };

    const hideTooltip = () => {
      tooltip.style.display = 'none';
    };

    const addStatusInfoIcon = () => {
      const headers = container.querySelectorAll('.p-datatable-thead th');

      headers.forEach((header) => {
        const headerText = header.textContent?.trim();

        if (!headerText.includes('Project Expense')) return;

        // Correct target from your DOM
        const targetDiv = header.querySelector('.header-column');

        if (!targetDiv) return;

        // Prevent duplicates
        if (targetDiv.querySelector('.project-expense-gm-info-icon')) return;

        const infoIcon = document.createElement('i');

        infoIcon.className
        = 'pi pi-info-circle project-expense-gm-info-icon';

        infoIcon.style.marginLeft = '18px';
        infoIcon.style.cursor = 'pointer';
        infoIcon.style.fontSize = '14px';

        infoIcon.addEventListener('mouseenter', showTooltip);
        infoIcon.addEventListener('mouseleave', hideTooltip);

        targetDiv.appendChild(infoIcon);
      });
    };

    const observer = new MutationObserver(() => {
      addStatusInfoIcon();
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    addStatusInfoIcon();

    return () => {
      observer.disconnect();

      tooltip.remove();
    };
  }, []);

  const extractMonthRangePayload = (container) => {
    const inputs = Array.from(container?.querySelectorAll('input') || []);
    const [fromInput, toInput] = inputs;

    return {
      fromMonthYear: toApiMonthRangeValue(fromInput?.value || null),
      toMonthYear: toApiMonthRangeValue(toInput?.value || null, { useEndOfMonth: true }),
    };
  };

  // Reset the month range ref to nulls when the toolbar reset or filter reset button is clicked
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleResetClick = (event) => {
      const isToolbarReset = event.target.closest('button')?.querySelector('.pi-refresh');
      const isFilterReset = event.target.closest('.tu-filter-footer-link');
      if (isToolbarReset || isFilterReset) {
        setSelectedMonthRange({ fromMonthYear: null, toMonthYear: null });
        tableConfigInjected.current = false;
      }
    };

    container.addEventListener('click', handleResetClick, true);
    return () => container.removeEventListener('click', handleResetClick, true);
  }, [setSelectedMonthRange]);

  useEffect(() => {
    // Month range calendar handler: capture Selected Month Range (From/To Month & Year)
    // from the month-wise-calendar-filter-container and send it in the API payload.
    const clearSelectedMonthRange = () => {
      setSelectedMonthRange({ fromMonthYear: null, toMonthYear: null });
    };

    const syncSelectedMonthRange = (container) => {
      const nextRange = extractMonthRangePayload(container);
      setSelectedMonthRange(nextRange);
      return nextRange;
    };

    const handleMonthWiseCalendarApplyFlow = (event) => {
      const footerApply = event.target.closest('.tu-filter-footer-apply');
      if (footerApply) {
        // If the Month & Year sub-popup is still open when the main Apply is
        // clicked, sync the latest input values before the API call proceeds.
        const calendarContainer = document.querySelector('.month-wise-calendar-filter-container');
        if (calendarContainer) {
          syncSelectedMonthRange(calendarContainer);
        }
        return;
      }

      const footerReset = event.target.closest('.tu-filter-footer-reset');
      if (footerReset) {
        clearSelectedMonthRange();
        return;
      }

      const calendarButton = event.target.closest('.month-wise-calendar-filter-container .p-button');
      if (!calendarButton) return;

      const monthContainer = calendarButton.closest('.month-wise-calendar-filter-container');
      const labelEl = calendarButton.querySelector('.p-button-label');
      const buttonLabel = labelEl?.textContent?.trim();

      if (buttonLabel === 'Reset') {
        clearSelectedMonthRange();
        setTimeout(() => {
          const mainResetButton = document.querySelector('.tu-filter-footer-reset');
          if (mainResetButton) {
            mainResetButton.click();
          }
        }, 50);
        return;
      }

      if (buttonLabel !== 'Apply') return;

      syncSelectedMonthRange(monthContainer);
    };

    document.addEventListener('click', handleMonthWiseCalendarApplyFlow, true);
    return () => document.removeEventListener('click', handleMonthWiseCalendarApplyFlow, true);
  }, [setSelectedMonthRange]);

  useEffect(() => {
    // Auto-open the first date input when Month & Year popup is rendered,
    // so users see the calendar immediately after selecting the filter option.
    const observer = new MutationObserver(() => {
      const container = document.querySelector('.calendar-filter-container');
      if (container && container.dataset.autoOpened !== 'true') {
        const input = container.querySelector('input');
        if (!input) return;

        container.dataset.autoOpened = 'true';
        setTimeout(() => {
          input.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
          input.focus();
          input.click();
        }, 0);
        return;
      }

      const menu = document.querySelector('.responsive-tiered-menu1, .p-tieredmenu');
      if (!menu || menu.dataset.autoOpened === 'true') return;

      const monthInput = menu.querySelector('.p-calendar input');
      if (!monthInput) return;

      menu.dataset.autoOpened = 'true';
      setTimeout(() => {
        monthInput.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
        monthInput.focus();
        monthInput.click();
      }, 0);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const watchedPickers = new WeakSet();
    const pickerObservers = [];

    // Reads the year the picker panel is currently showing. Returns null when it can't be
    // determined so callers can fail "open" instead of reusing a stale disabled state.
    const getDisplayedYear = (monthPicker) => {
      const titleEl = monthPicker.querySelector(
        '.p-datepicker-title, .p-datepicker-year, .p-datepicker-header',
      );
      const yearText = titleEl?.textContent?.trim();
      const parsedYear = Number(yearText?.match(/\d{4}/)?.[0]);

      return Number.isNaN(parsedYear) ? null : parsedYear;
    };

    // A month is only ever "future" relative to the real current date, never relative to
    // whatever year/month was disabled the last time the panel rendered.
    const isMonthDisabled = (displayedYear, monthIndex) => {
      if (displayedYear === null) return false;
      if (displayedYear !== currentYear) return displayedYear > currentYear;

      return monthIndex > currentMonth;
    };

    // PrimeReact re-renders the month grid using `data-p-disabled`/`class` attributes it owns,
    // which silently overwrite our patch. Re-applying only on real state changes (guarded below)
    // lets a dedicated per-picker observer correct it immediately without looping forever.
    const applyMonthDisabledState = (monthPicker) => {
      const displayedYear = getDisplayedYear(monthPicker);

      monthPicker.querySelectorAll('.p-monthpicker-month').forEach((monthElement, index) => {
        const isDisabled = isMonthDisabled(displayedYear, index);
        const alreadyApplied = monthElement.classList.contains('p-disabled') === isDisabled
          && monthElement.getAttribute('data-p-disabled') === `${isDisabled}`;

        if (alreadyApplied) return;

        monthElement.classList.toggle('p-disabled', isDisabled);
        monthElement.setAttribute('aria-disabled', `${isDisabled}`);
        monthElement.setAttribute('data-p-disabled', `${isDisabled}`);
        monthElement.style.pointerEvents = isDisabled ? 'none' : '';
        monthElement.style.opacity = isDisabled ? '0.35' : '1';
      });

      // Users must always be able to navigate forward to see future years, even
      // though the months inside those years remain disabled.
      const nextYearButton = monthPicker.querySelector('.p-datepicker-next');
      if (nextYearButton && (nextYearButton.disabled || nextYearButton.classList.contains('p-disabled'))) {
        nextYearButton.disabled = false;
        nextYearButton.classList.remove('p-disabled');
        nextYearButton.setAttribute('aria-disabled', 'false');
        nextYearButton.setAttribute('data-p-disabled', 'false');
        nextYearButton.style.pointerEvents = 'auto';
        nextYearButton.style.opacity = '1';
      }
    };

    // Attach one observer per picker panel so re-renders are caught the instant they happen,
    // instead of relying on a fixed delay that can race with PrimeReact's own state updates.
    const watchMonthPicker = (monthPicker) => {
      if (watchedPickers.has(monthPicker)) return;
      watchedPickers.add(monthPicker);

      applyMonthDisabledState(monthPicker);

      const pickerObserver = new MutationObserver(() => applyMonthDisabledState(monthPicker));
      pickerObserver.observe(monthPicker, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'data-p-disabled', 'aria-disabled', 'disabled'],
      });
      pickerObservers.push(pickerObserver);
    };

    const scanForMonthPickers = () => {
      document.querySelectorAll('.month-wise-calendar-filter-container').forEach(watchMonthPicker);
    };

    // Strict click guard fallback processor
    const blockFutureMonthClicks = (event) => {
      const monthElement = event.target.closest(
        '.month-wise-calendar-filter-container .p-monthpicker-month',
      );
      if (!monthElement) return;

      const monthPicker = monthElement.closest('.month-wise-calendar-filter-container');
      const monthElements = Array.from(monthPicker.querySelectorAll('.p-monthpicker-month'));
      const index = monthElements.indexOf(monthElement);
      const displayedYear = getDisplayedYear(monthPicker);

      if (isMonthDisabled(displayedYear, index)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    };

    // Observe the document to discover newly opened calendar panels
    const bodyObserver = new MutationObserver(scanForMonthPickers);
    bodyObserver.observe(document.body, { childList: true, subtree: true });
    scanForMonthPickers();

    // Listen to button clicks directly to re-verify state cleanly
    document.addEventListener('mousedown', blockFutureMonthClicks, true);
    document.addEventListener('click', blockFutureMonthClicks, true);

    return () => {
      bodyObserver.disconnect();
      pickerObservers.forEach((pickerObserver) => pickerObserver.disconnect());
      document.removeEventListener('mousedown', blockFutureMonthClicks, true);
      document.removeEventListener('click', blockFutureMonthClicks, true);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    import('../../../Assets/images/json/dar_reseller_report_load.json').then(
      ({ default: data }) => {
        if (!cancelled) setAnimationData(data);
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!animationData || !animationContainer.current || hasAppliedFilters) return undefined;
    const animation = lottie.loadAnimation({
      container: animationContainer.current,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData,
    });
    return () => animation.destroy();
  }, [animationData, hasAppliedFilters, loading]);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,
      userInfo: utilityUserInfo,
      apiEndpoints: {
        getAllViews: {
          endpoint: '',
          enabled: false,
        },
        getFilteredData: {
          endpoint: '',
          enabled: true,
        },
        saveOrUpdateView: {
          endpoint: '',
          enabled: false,
        },
        deleteView: {
          endpoint: '',
          enabled: false,
        },
        download: {
          ignoreRoundOff: true,
          endpoint: 'export',
          enabled: true,
          filename: buildSelectedProjectExportFileName,
          sheetName: buildSelectedProjectExportSheetName,
          reportTitle: buildSelectedProjectExportFileName,
        },
      },
      removecommaseperation: true,
      handler: {
        onFilterUpdate: ({ action, filters }) => {
          const safeFilters = Array.isArray(filters) ? filters : [];
          if (action === 'apply') {
            const monthFilter = safeFilters.find((data) => data.field === 'month');
            const hasFrom = monthFilter?.range?.[0];
            const hasTo = monthFilter?.range?.[1];
            if (!hasFrom || !hasTo) {
              toast.error('Please select month range before applying.');
              return;
            }
            setLoading(true);
            setAppliedFilters(safeFilters);
            setHasAppliedFilters(true);
          } else if (action === 'reset') {
            setLoading(false);
            setAppliedFilters([]);
            setHasAppliedFilters(false);
          }
        },
        onCellClickHandler: handleCellClick,
        handleFilteredApiResponse,
      },
      additionalPayloadData: {
        filter: appliedFilters,
        isFirstLoad: isFirstLoad,
        get fromMonthYear() {
          return selectedMonthRangeRef.current.fromMonthYear;
        },
        get toMonthYear() {
          return selectedMonthRangeRef.current.toMonthYear;
        },
      },
      headers: {
        token: userProfile?.access_token,
      },
      showToolBar: true,
      showPagination: false,
      toolBarSettings: {
        showFilterOption: true,
        showAddNew: false,
        showDownloadExcel: true,
        downloadExcelFileName: buildSelectedProjectExportFileName,
        downloadExcelSheetName: buildSelectedProjectExportSheetName,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: <ToolBarHeading />,
        showSaveView: false,
        showReset: false,
        showExpandCollapse: false,
      },
    },
    tableSettings: {
      size: 'small',
      pageSize: pagination.limit,
      loaderSkeleton: { rows: 10, bodyCells: 2, columns: 5 },
      paginationConfig: {
        ...pagination,
        onPaginationChange: handlePaginationChange,
      },
      showExpandCollapse: false,
      enableTreeTable: false,
      tableScrollHeight,
      maxFrozenColumns: {
        desktop: 1,
        tablet: 1,
        mobile: 1,
      },
    },
  }), [appliedFilters]);

  if (!userProfile?.access_token) {
    return null;
  }

  return (
    <>
      <div
        className={`gross-margin-outer-layer${hasAppliedFilters ? '' : ' gross-margin-awaiting-project'}`}
        ref={containerRef}
      >
        <div className="gross-margin-table-container">
          <div className="gross-margin-table-utility" ref={tableContainerRef}>
            <TableUtility
              configuration={configuration}
            />
            {loading && <LocalSkeleton />}
          </div>
          {!loading && !hasAppliedFilters && (
            <GrossMarginReportInitialState animationContainer={animationContainer} />
          )}
        </div>
      </div>
      <Tooltip
        target=".project-expense-gm-info-icon"
        className="project-expense-tooltip"
      />
    </>
  );
};

export default GrossMarginReport;
